import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [agents, setAgents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);

  // تحميل البيانات من Supabase
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: agentsData }, { data: sessionsData }, { data: settingsData }] = await Promise.all([
        supabase.from('agents').select('*').order('created_at', { ascending: false }),
        supabase.from('sessions').select('*').order('created_at', { ascending: false }),
        supabase.from('settings').select('*'),
      ]);
      setAgents(agentsData || []);
      setSessions(sessionsData || []);
      // جلب مفتاح Gemini من Supabase
      const geminiKey = settingsData?.find(s => s.key === 'gemini_api_key')?.value || '';
      if (geminiKey) setApiKey(geminiKey);
      else setApiKey(localStorage.getItem('ux_api_key') || '');
    } catch (e) {
      console.error('Load error:', e);
      setApiKey(localStorage.getItem('ux_api_key') || '');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { localStorage.setItem('ux_api_key', apiKey); }, [apiKey]);

  const saveApiKey = async (key) => {
    setApiKey(key);
    localStorage.setItem('ux_api_key', key);
    await supabase.from('settings').upsert({ key: 'gemini_api_key', value: key });
  };

  // ---- AGENTS ----
  const addAgent = async (agent) => {
    const newAgent = {
      name: agent.name,
      objective: agent.objective,
      prompt: agent.prompt,
      language: agent.language || 'ar',
      question_count: agent.questionCount || 7,
      output_fields: agent.outputFields || [],
      active: true,
    };
    const { data, error } = await supabase.from('agents').insert([newAgent]).select().single();
    if (!error && data) {
      setAgents(prev => [data, ...prev]);
      return data;
    }
    return null;
  };

  const updateAgent = async (id, updates) => {
    const dbUpdates = {};
    if (updates.name) dbUpdates.name = updates.name;
    if (updates.objective) dbUpdates.objective = updates.objective;
    if (updates.prompt) dbUpdates.prompt = updates.prompt;
    if (updates.language) dbUpdates.language = updates.language;
    if (updates.questionCount) dbUpdates.question_count = updates.questionCount;
    if (updates.outputFields) dbUpdates.output_fields = updates.outputFields;
    if (updates.active !== undefined) dbUpdates.active = updates.active;

    const { data, error } = await supabase.from('agents').update(dbUpdates).eq('id', id).select().single();
    if (!error && data) setAgents(prev => prev.map(a => a.id === id ? data : a));
  };

  const deleteAgent = async (id) => {
    await supabase.from('sessions').delete().eq('agent_id', id);
    await supabase.from('agents').delete().eq('id', id);
    setAgents(prev => prev.filter(a => a.id !== id));
    setSessions(prev => prev.filter(s => s.agent_id !== id));
  };

  const getAgent = (id) => {
    const agent = agents.find(a => a.id === id);
    if (!agent) return null;
    // توحيد الحقول
    return {
      ...agent,
      questionCount: agent.question_count,
      outputFields: agent.output_fields,
    };
  };

  // ---- SESSIONS ----
  const addSession = async (agentId) => {
    const newSession = {
      agent_id: agentId,
      status: 'in-progress',
      messages: [],
      analysis: null,
      duration_minutes: null,
    };
    const { data, error } = await supabase.from('sessions').insert([newSession]).select().single();
    if (!error && data) {
      setSessions(prev => [data, ...prev]);
      return data;
    }
    return null;
  };

  const updateSession = async (id, updates) => {
    const dbUpdates = {};
    if (updates.status) dbUpdates.status = updates.status;
    if (updates.messages) dbUpdates.messages = updates.messages;
    if (updates.analysis !== undefined) dbUpdates.analysis = updates.analysis;
    if (updates.durationMinutes !== undefined) dbUpdates.duration_minutes = updates.durationMinutes;

    const { data, error } = await supabase.from('sessions').update(dbUpdates).eq('id', id).select().single();
    if (!error && data) setSessions(prev => prev.map(s => s.id === id ? data : s));
  };

  const getSession = (id) => {
    const s = sessions.find(s => s.id === id);
    if (!s) return null;
    return { ...s, agentId: s.agent_id, durationMinutes: s.duration_minutes };
  };

  const getAgentSessions = (agentId) => sessions.filter(s => s.agent_id === agentId);

  const deleteSession = async (id) => {
    await supabase.from('sessions').delete().eq('id', id);
    setSessions(prev => prev.filter(s => s.id !== id));
  };

  const stats = {
    total: sessions.length,
    completed: sessions.filter(s => s.status === 'completed').length,
    activeAgents: agents.filter(a => a.active).length,
    painPoints: sessions.filter(s => s.analysis?.painPoints).reduce((acc, s) => acc + (s.analysis.painPoints.length || 0), 0),
  };

  return (
    <AppContext.Provider value={{
      agents, addAgent, updateAgent, deleteAgent, getAgent,
      sessions, addSession, updateSession, deleteSession, getSession, getAgentSessions,
      apiKey, setApiKey, saveApiKey, stats, loading, loadData,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
